import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import practicingSimba from '../../../../../shared/assets/simba-ai/practicing.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { Icons } from '../../../../../shared/utils/icons.jsx'
import { formatDate, getTimePassedFrom } from '../../../../../shared/utils/date-time-utilities.jsx'
import LatestPracticeStreak from '../LatestPracticeStreak'
import sadSimba from '../../../../../shared/assets/simba-ai/sad.png'
import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import CustomChip from '../../../../../shared/components/common/CustomChip'

const PracticeModeHome = observer( ( { enterFullScreen } ) => {


    const { palette, layout, border, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const fetchUserPracticeHistory = async () => {
        SimbaAIStore.setView( "history" )
        await SimbaAIStore.fetchUserHistory()
    }

    const startQuiz = async ( practiceSession ) => {
        setSearchParamsValues( { practiceQuiz: practiceSession._id } )

        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceSession._id )

        if ( success ) {
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        enterFullScreen()
                    }
                }
            }
        }
    }

    const repeatLastOne = () => {
        const lastAttemptDetails = SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" )
        startQuiz( lastAttemptDetails.practice_session )
    }

    const showSubjectPracticeSessions = ( subject ) => {
        setSearchParamsValues( { selectedSubject: subject.subject_code } )
        SimbaAIStore.setView( "subject-practice-sessions" )
    }

    useEffect( () => {
        const getHistory = () => {
            SimbaAIStore.fetchUserHistory()
        }
        getHistory()
    }, [SimbaAIStore] )

    return (
        <Box
            sx={{
                display: "flex",
                gap: layout.sectionsGap,
                flexDirection: "column",
                flexGrow: 1
            }}>
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    padding: layout.pagePadding,
                    overflow: "auto"
                }}>
                {SimbaAIStore.getPracticeSessions.length > 0 && <LatestPracticeStreak />}
                <Box
                    sx={{
                        width: "100%",
                        bgcolor: palette.primary.light,
                        borderRadius: "10px",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { sm: "40px", xs: "10px" },
                        padding: "20px",
                        alignItems: "center"
                    }}>
                    <Box component="img" src={practicingSimba} sx={{
                        height: { sm: "140px", xs: "100px" }
                    }} />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: { xs: "center", sm: "flex-start" }
                        }}>
                        <Typography
                            color={palette.common.white}
                            variant='h5'
                            component="p"
                            sx={{
                                textAlign: { xs: "center", sm: "left" },
                                fontSize: { sm: "28px", xs: "20px" },
                                fontWeight: "800"
                            }}> <Typography
                            component="span"
                            sx={{
                                fontSize: "inherit",
                                fontWeight: "inherit"
                            }}>Simba AI</Typography> Welcomes You to Practice Zone </Typography>
                        <Typography
                            color={palette.common.white}
                            component="p"
                            sx={{
                                textAlign: { xs: "center", sm: "left" },
                                display: "block",
                                maxWidth: "450px",
                                fontSize: "12px"
                            }}>Get ready for an interactive AI-powered quiz experience created from your own course material. Let’s sharpen your skills, question by question!</Typography>
                        <Box
                            sx={{
                                flexWrap: "wrap",
                                flexGrow: { xs: "1", sm: "initial" },
                                marginTop: "30px",
                                display: "flex",
                                gap: layout.cardsGap,
                                alignItems: "center"
                            }}>
                            <Button
                                className="animation-wave"
                                onClick={fetchUserPracticeHistory}
                                sx={{
                                    border: `1px solid ${palette.common.white}`,
                                    borderBottom: `4px solid ${palette.common.white}`,
                                    color: palette.common.white,
                                    borderRadius: "10px"
                                }}
                                startIcon={Icons.default.History}
                                variant='outlined'
                                color='primary'
                                disableElevation
                            >
                                View Practice History
                            </Button>
                        </Box>
                    </Box>

                </Box>
                {SimbaAIStore.getTopicsAvailableSubjects.length > 0 && <Box sx={{
                    width: "100%"
                }}>
                    <Typography
                        color='textSecondary'
                        component="p"
                        sx={{
                            fontSize: "14px",
                            marginY: "10px",
                            fontWeight: 600
                        }}>
                        Select a subject below to get started
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" },
                            gap: "10px",
                            flexWrap: "wrap",
                            alignItems: "center"
                        }}>
                        {SimbaAIStore.getTopicsAvailableSubjects.map( subject => (
                            <Box
                                onClick={() => showSubjectPracticeSessions( subject )}
                                component="button"
                                key={subject.subject_code}
                                sx={{
                                    flexGrow: { xs: 1, sm: "initial" },
                                    overflow: "hidden",
                                    display: "flex",
                                    gap: "20px",
                                    alignItems: "center",
                                    padding: "10px 20px",
                                    borderRadius: "10px",
                                    boxShadow: shadows[1],
                                    border: border[1],
                                    borderBottom: `4px solid ${palette.borderColor}`,
                                    position: "relative",
                                    bgcolor: palette.contentBg,
                                    cursor: "pointer",
                                    transition: "200ms",

                                    ":hover": {
                                        borderColor: palette.primary.light,
                                        transform: `skew(-5deg)`
                                    }
                                }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexShrink: 0,
                                        fontSize: "20px",
                                        justifyContent: "center",
                                        width: "40px",
                                        borderRadius: "50%",
                                        bgcolor: palette.primary.dark,
                                        color: palette.primary.contrastText,
                                        alignItems: "center",
                                        height: "100%",
                                        aspectRatio: "1 / 1"
                                    }}>{Icons.default.Subjects}</Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        minWidth: 0,
                                        overflow: "hidden",
                                        flexDirection: "column",
                                        alignItems: "flex-start"
                                    }}>
                                    <Tooltip title={subject.subject_name}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                textAlign: "left",
                                                width: "100%",
                                                fontSize: { xs: "14px", sm: "18px" },
                                                fontWeight: 600
                                            }}>{subject.subject_name}</Typography>
                                    </Tooltip>
                                    <Typography
                                        component="em"
                                        sx={{
                                            display: "block",
                                            marginTop: "-3px",
                                            fontSize: "12px"
                                        }}>{subject.subject_code}</Typography>
                                </Box>
                            </Box>
                        ) )}
                    </Box>
                </Box>}
                {SimbaAIStore.getPracticeSessions.length > 0 && <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        marginTop: "10px"
                    }}>
                    <Typography
                        color='textSecondary'
                        component="p"
                        sx={{
                            fontSize: "14px",
                            marginY: "10px",
                            fontWeight: 600
                        }}>
                        Or select a recently added practice session
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" },
                            flexWrap: "wrap",
                            width: "100%",
                            gap: layout.cardsGap
                        }}>
                        {SimbaAIStore.getPracticeSessions.slice( 0, 3 ).map( practiceSession => {
                            const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()
                            const modules = Object.keys( practiceSession.modules_and_topics )
                            return (
                                <Box
                                    component="button"
                                    onClick={() => startQuiz( practiceSession )}
                                    key={practiceSession._id}
                                    sx={{
                                        flexGrow: { xs: 1, sm: "initial" },
                                        overflow: "hidden",
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "flex-start",
                                        padding: "10px 20px",
                                        borderRadius: "10px",
                                        boxShadow: shadows[1],
                                        border: border[1],
                                        borderBottom: `4px solid ${palette.borderColor}`,
                                        position: "relative",
                                        bgcolor: palette.contentBg,
                                        cursor: "pointer",
                                        transition: "200ms",

                                        ":hover": {
                                            borderColor: palette.primary.light,
                                            transform: `skew(-5deg)`
                                        }
                                    }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            fontSize: "20px",
                                            color: palette.primary.contrastText,
                                            justifyContent: "center",
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                            bgcolor: palette.primary.dark,
                                            alignItems: "center",
                                            aspectRatio: "1 / 1"
                                        }}>{Icons.default.QuizIconOutlined}</Box>
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            minWidth: 0
                                        }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                width: "100%",
                                                justifyContent: "space-between",
                                                gap: "10px"
                                            }}>
                                            <Typography variant='caption' color='textSecondary' noWrap  >{formatDate( practiceSession.date_range_start )} to {formatDate( practiceSession.date_range_end )}</Typography>
                                            <Typography variant='caption' color='textSecondary' noWrap  >{getTimePassedFrom( practiceSession.created_at )}</Typography>
                                        </Box>
                                        <Tooltip placement='top' disableInteractive title={practiceSession.title}>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    textAlign: "left",
                                                    fontSize: { xs: "16px", sm: "18px" },
                                                    minWidth: 0,
                                                    fontWeight: 600
                                                }}>{practiceSession.title}  </Typography>
                                        </Tooltip>

                                        <Typography
                                            noWrap
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                fontSize: "12px"
                                            }}> {Icons.default.Sort2} {`Difficulty level: ${practiceSession.complexity}`}</Typography>
                                        <Typography
                                            noWrap
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                fontSize: "12px"
                                            }}> {Icons.default.User} {practiceSession.creator_info.name}</Typography>
                                        <Typography
                                            noWrap
                                            component="em"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                fontSize: "12px"
                                            }}> {Icons.default.Subjects} {practiceSession.subject?.subject_name} - {modules.join( ", " )}</Typography>
                                        <Typography
                                            component="div"
                                            noWrap
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                fontSize: "12px"
                                            }}> {Icons.default.Category} {topics.slice( 0, 2 ).map( topic => <CustomChip tooltipTitle={"Practice topic"} value={topic} key={topic} /> )}{topics.length > 2 ? <CustomChip tooltipTitle={topics.slice( 2 ).join( ", " )} value={`+${topics.length - 2} More.`} /> : ""}</Typography>
                                        {/* <Box color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                            {topics.slice( 0, 2 ).map( ( topic, index ) => (
                                                <Typography title={topic} maxWidth="100px" noWrap fontSize="12px" key={topic} borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >{topic}</Typography>
                                            ) )}
                                            {topics.length > 2 && <Typography fontSize="12px" borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >+{topics.length - 2} More</Typography>}
                                        </Box> */}
                                    </Box>
                                </Box>
                            );
                        } )}
                    </Box>
                </Box>}
                {SimbaAIStore.getPracticeSessions.length === 0 && <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        border: `2px dashed ${palette.borderColor}`,
                        borderRadius: "20px",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "20px",
                        gap: "10px",
                        justifyContent: "center"
                    }}>
                    <Box
                        sx={{
                            position: "absolute",
                            height: "60%",
                            maxHeight: "400px",
                            bgcolor: palette.contentBg,
                            borderRadius: "50%",
                            aspectRatio: "1 / 1",
                            zIndex: 1
                        }} />
                    <Box
                        component="img"
                        src={sadSimba}
                        alt="No practice sessions"
                        sx={{
                            position: "relative",
                            height: "180px",
                            zIndex: 10
                        }} />
                    <Typography
                        variant="h5"
                        component="p"
                        sx={{
                            position: "relative",
                            textAlign: "center",
                            zIndex: 10
                        }}>No practice sessions to show!</Typography>
                </Box>}
            </Box>
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    position: "sticky",
                    bottom: "0",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: `4px dashed ${palette.borderColor}`,
                    bgcolor: palette.contentBg,
                    padding: { md: "10px 20px", xs: "10px" },
                    zIndex: 10
                }}>
                {Boolean( SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" ) ) && <Button className='animation-wave' onClick={repeatLastOne} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Replay} variant='outlined' color='primary' >Repeat last practice</Button>}
                {!Boolean( SimbaAIStore.getPracticeHistory.find( item => item.status === "completed" ) ) && <Box />}
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                    <Button onClick={() => setSearchParamsValues( { simbaAIOpen: "", simbaAIMode: "" } )} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Quit Simba AI</Button>
                </Box>
            </Box>
        </Box >
    );
} )

export default PracticeModeHome
